interface IResponsiveSetting {
    breakpoint: number;
    settings: {
        slidesToShow?: number;
        slidesToScroll?: number;
        variableWidth?: boolean;
        [key: string]: unknown;
    };
}

interface ISliderSettings {
    slidesToShow: number;
    slidesToScroll: number;
    variableWidth?: boolean;
    responsive?: IResponsiveSetting[];
    [key: string]: unknown;
}

/**
 * Adjusts slider settings based on the number of items to ensure
 * proper left alignment when there are fewer items than slidesToShow.
 *
 * When itemCount <= slidesToShow, enables variableWidth to prevent
 * centering of items and aligns them to the left.
 */
export const getAdjustedSliderSettings = <T extends ISliderSettings>(baseSettings: T, itemCount: number): T => {
    if (itemCount <= 0) return baseSettings;

    const adjustResponsiveSetting = (setting: IResponsiveSetting): IResponsiveSetting => {
        // If slidesToShow is not defined in this responsive setting, skip adjustment
        if (setting.settings.slidesToShow === undefined) {
            return setting;
        }

        const needsVariableWidth = itemCount <= setting.settings.slidesToShow;
        return {
            ...setting,
            settings: {
                ...setting.settings,
                variableWidth: needsVariableWidth || setting.settings.variableWidth,
                slidesToShow: needsVariableWidth ? itemCount : setting.settings.slidesToShow,
                slidesToScroll: setting.settings.slidesToScroll !== undefined ? Math.min(setting.settings.slidesToScroll, itemCount) : undefined
            }
        };
    };

    const needsVariableWidth = itemCount <= baseSettings.slidesToShow;
    return {
        ...baseSettings,
        variableWidth: needsVariableWidth || baseSettings.variableWidth,
        slidesToShow: needsVariableWidth ? itemCount : baseSettings.slidesToShow,
        slidesToScroll: Math.min(baseSettings.slidesToScroll, itemCount),
        responsive: baseSettings.responsive?.map(adjustResponsiveSetting)
    } as T;
};
